

import React from 'react'
import { GeolocationType, Geolocation } from './types'
import { geocode, getReverseGeocodeResult } from '../googlemaps'
import { geolocated, GeolocatedConfig, GeolocatedProps } from 'react-geolocated'
import { ApolloClient } from '@apollo/client'

interface GetGeolocationProps extends GeolocatedProps {
  client: ApolloClient,
  setError: (title: string, message: string) => void
  type: GeolocationType
  fieldName: string
  reportGeolocation?: (location: Geolocation, name: string) => void
  address?: string
  city?: string
  zipcode?: string
}

interface GetGeolocationState {
  time: number | null
}

interface GeolocationCoordinates {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}

class GetGeolocation extends React.Component<GetGeolocationProps, GetGeolocationState> {
  public errorCount: number = 0
  public geolocated: boolean = false
  public timerId: number | null = null

  public constructor(props: GetGeolocationProps) {
    super(props)
    this.state = { time: null }
  }

  public tick() {
    this.setState({ time: new Date().getSeconds() })
  }

  public componentDidMount() {
    if (this.props.type === GeolocationType.geocode) {
      return
    }

    if (!this.props.isGeolocationAvailable) {
      return this.props.setError('Virhe', 'Laitteesi ei tue sijaintitiedon hakemista, syötä tiedot käsin')
    }

    if (!this.props.isGeolocationEnabled) {
      return this.props.setError('Virhe', 'Laitteen sijaintitietoa ei saatavilla, sijainti ei ole käytössä')
    }
  }

  public async componentDidUpdate() {
    if (this.geolocated) return

    if (!this.props.isGeolocationAvailable) {
      this.errorCount = this.errorCount + 1
      if (this.errorCount >= 10) {
        this.clearInterval()
        return this.props.setError('Virhe', 'Laitteesi ei tue sijaintitiedon hakemista, syötä tiedot käsin')
      }
    }

    if (!this.props.isGeolocationEnabled) {
      this.errorCount = this.errorCount + 1

      if (this.errorCount >= 10) {
        this.clearInterval()
        return this.props.setError('Virhe', 'Laitteen sijaintitietoa ei saatavilla, sijainti ei ole käytössä')
      }
    }

    this.geolocated = this.props.coords ? true : false

    if (this.props.coords && this.props.type === GeolocationType.reverseGeocode) {
      const result = await this.reverseGeocode(this.props.coords)

      if (this.props.reportGeolocation && result) {
        const { address, city, zipcode } = result
        this.props.reportGeolocation(
          toGeolocationResponse(this.props.coords, { address, city, zipcode }),
          this.props.fieldName
        )
        return
      }
    }

    if (this.props.coords && this.props.type === GeolocationType.geolocation) {
      if (this.props.reportGeolocation) {
        this.props.reportGeolocation(toGeolocationResponse(this.props.coords, null), this.props.fieldName)
        return
      }
    }

    if (this.props.type === GeolocationType.geocode && this.props.address && this.props.city) {
      const { address, city, fieldName, zipcode } = this.props
      const result = await this.geocode(address, city)

      if (this.props.reportGeolocation && result) {
        this.props.reportGeolocation(
          {
            address,
            city,
            zipcode: zipcode || '',
            coords: {
              lat: result.lat,
              long: result.long,
            },
          },
          fieldName
        )
        return
      }
    }
  }

  public componentWillUnmount() {
    this.clearInterval()
  }

  public clearInterval() {
    if (!this.timerId) return
    clearInterval(this.timerId)
  }

  public async reverseGeocode(coords: GeolocationCoordinates | undefined) {
    if (!coords) return null

    const result = await getReverseGeocodeResult({ lat: coords.latitude, lng: coords.longitude })
    if (!result) {
      return this.props.setError('Virhe', 'Sijainnin paikannus epännistui')
    }

    return result
  }

  public async geocode(address: string, city: string) {
    const results = await geocode({ address: `${address}, ${city}` })

    if (!results) return null

    return {
      lat: results[0].geometry.location.lat(),
      long: results[0].geometry.location.lng(),
    }
  }

  public render() {
    return null
  }
}

const geolocatedOptions: GeolocatedConfig = {
  positionOptions: {
    enableHighAccuracy: process.env.NODE_ENV === 'development' ? false : true,
    maximumAge: 0,
    timeout: 10000,
  },
  watchPosition: true,
  userDecisionTimeout: 10000,
  isOptimisticGeolocationEnabled: true,
}

export default geolocated(geolocatedOptions)(GetGeolocation)


function toGeolocationResponse(
  coords: GeolocationCoordinates,
  result: { address: string; city: string; zipcode: string } | null 
): Geolocation {
  const { latitude, longitude } = coords
  const city = result ? result.city : ''
  const address = result ? result.address : ''
  const zipcode = result && result.zipcode ? result.zipcode : ''

  return {
    address,
    city,
    zipcode,
    coords: {
      lat: latitude,
      long: longitude,
    },
  }
}
